import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography,  Accordion, AccordionSummary, AccordionDetails, Tooltip } from '@mui/material';
import { AgGridTable } from 'components/tables/AgGridTable';
import { Link, useParams } from 'react-router-dom';
import StatusText from 'common/component/StatusText';
import { fetchProductActivityLogs } from '../../../redux/slices/products';
import { formatTimestampToDateView } from 'utilities/common';
import OrderProductLogsActivity from './OrderProductLogsActivity';
import { statusObj } from './Statusobj';
import { GridExpandMoreIcon } from '@mui/x-data-grid';

export default function ProductActivityLogs({inventoryVersion}) {
  const dispatch = useDispatch();
  const { loading = false, activityLogs = {} } = useSelector(state => state.products);
  const pageRef = useRef(0);
  const searchTermRef = useRef('');
  const mainGridApiRef = useRef('');
  const gridRefApi = useRef(null);
  const { productId = '' } = useParams();
  const [orderActivityLog, setOrderActivityLog] = useState('batch_logs')
  const [isExpanded, setIsExpanded] = useState(false)

  const handleAccordionToggle = () => {
    setIsExpanded(!isExpanded)
    pageRef.current =0
  }

  useEffect(() => {
    if (mainGridApiRef.current) {
    pageRef.current = 0;
    searchFn()
    }
  },[inventoryVersion])

  const columnDefs = [
    {
      headerName: 'Date/Time',
      field: 'createdDate',
      suppressMenu: false,
      sortable: false,
      cellRenderer: (props) => {
        return props?.value ? formatTimestampToDateView(props?.value) : '-';
      },
    },
    {
      headerName: 'Updated By',
      field: 'updatedBy',
      key: 'updatedBy',
      suppressMenu: false,
      sortable: false,
      cellRenderer: (props) => {
        return props?.value ? props?.value : '-';
      },
    },
    {
      headerName: 'Batch',
      field: 'productBatchId',
      key: 'productBatchId',
      suppressMenu: false,
      sortable: false,
    cellRenderer: (props) => {
      const [tooltipMessage, setTooltipMessage] = useState("Click to copy"); // State for tooltip message
  
      if (!props?.data?._id) return;
  
      // Get the productBatchId value (either from previousChanges or fallback to '-')
      const batchId = props?.data?.previousChanges?.productBatchId || '-';
  
      return (
        <>
          {/* Tooltip with dynamic message */}
          <Tooltip title={tooltipMessage} arrow>
            <span
              style={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={() => {
                if (batchId !== '-') {
                  navigator.clipboard.writeText(batchId)
                    .then(() => {
                      setTooltipMessage("Copied!"); // Change tooltip message after successful copy
                      setTimeout(() => setTooltipMessage("Click to copy"), 2000); // Reset after 2 seconds
                    })
                    .catch((err) => {});
                }
              }}
            >
              {batchId}
            </span>
          </Tooltip>
        </>
      );
     }
     },
    {
      headerName: 'Order Id',
      field: '',
      key: '',
      suppressMenu: false,
      sortable: false,
      cellRenderer: (props) => {
        const orderId = props?.data?.previousChanges?.orderId;

        return orderId ? 
        
        (<Link to={`/orders/details/${orderId}?orderDetailsTab=orderDetails`}>
         {props?.data?.previousChanges?.orderId}
        </Link>)
        : "-";
      },
    },
    {
      headerName: 'Order Status',
      field: '',
      key: '',
      suppressMenu: false,
      sortable: false,
      cellRenderer: (props) => {
        return props?.data?.previousChanges?.orderStatus ? <>{statusObj[props?.data?.previousChanges?.orderStatus || 'awaiting_shipment']}</> : "-";
      },
    },
    {
      headerName: 'Source',
      field: 'source',
      suppressMenu: false,
      sortable: false

    },
  
    {
      headerName: 'Prev Data (Status/Qty)',
      field: '',
      suppressMenu: false,
      sortable: false,
      cellRenderer: (props) => {
        return props?.data?.previousChanges?.totalQuantity !== undefined
          ? props.data.previousChanges.totalQuantity.toString()
          : props?.data?.previousChanges?.oldStatus
            ? <StatusText status={props.data.previousChanges.oldStatus} />
            : props?.data?.previousChanges?.currentQuantity !== undefined
              ? props.data.previousChanges.currentQuantity
              : '-';
      },
    },
    {
      headerName: 'New Data (Status/Qty)',
      field: '',
      suppressMenu: false,
      sortable: false,
      headerClass: 'left-aligned-cell-header',
      cellRenderer: (props) => {
        // return props?.data?.newChanges?.totalQuantity !== undefined ? props?.data?.newChanges?.totalQuantity?.toString() : props?.data?.newChanges?.newStatus ? <StatusText status={props?.data?.newChanges?.newStatus} />: props?.data?.newChanges?.currentQuantity ?  props?.data?.newChanges?.currentQuantity : '-';
        return props?.data?.newChanges?.totalQuantity !== undefined
          ? props.data.newChanges.totalQuantity.toString()
          : props?.data?.newChanges?.newStatus
            ? <StatusText status={props.data.newChanges.newStatus} />
            : props?.data?.newChanges?.currentQuantity !== undefined
              ? props.data.newChanges.currentQuantity
              : '-';
      },
    },
  ];

  const getRows = async (params) => {
    gridRefApi.current = params;
    if (orderActivityLog === 'batch_logs') {
      dispatch(fetchProductActivityLogs({ skip: pageRef.current, limit: 20, searchTerm: searchTermRef.current, productId })).unwrap().then(res => {
        const { activityLogs, totalActivityLogs } = res;
        params.successCallback([...activityLogs], totalActivityLogs);
        pageRef.current = pageRef.current + 20;
      });
    }
    else {
      dispatch(fetchProductActivityLogs({ skip: pageRef.current, limit: 20, searchTerm: searchTermRef.current, productId })).unwrap().then(res => {
        const { individualSums
          , totalActivityLogs } = res;
        params.successCallback([...individualSums
        ], totalActivityLogs);
        pageRef.current = pageRef.current + 20;
      });
    }
  };

  const searchFn = () => {
    const dataSource = {
      getRows
    };
    mainGridApiRef.current.ensureIndexVisible(0, null);
    mainGridApiRef.current.setGridOption('datasource', dataSource);
  };

  useEffect(() => {
    if (mainGridApiRef?.current) {
      if (loading) {
        mainGridApiRef.current.showLoadingOverlay();
      } else if (activityLogs?.total === 0) {
        mainGridApiRef.current.showNoRowsOverlay();
      }
      else {
        mainGridApiRef.current.hideOverlay();
      }
    }
  }, [activityLogs, loading]);

  //  order logs
  const columnOrderDefs = [
    {
      headerName: 'Date/Time',
      field: 'date',
      suppressMenu: false,
      sortable: false,
      cellRenderer: (props) => {
        return props?.value ? formatTimestampToDateView(props?.value) : '';
      },
    },
    {
      headerName: 'Order ID',
      field: 'orderId',
      key: 'orderId',
      suppressMenu: false,
      sortable: false,
    },
    {
      headerName: 'Total Quantity',
      field: 'totalQuantity',
      suppressMenu: false,
      sortable: false

    },
    {
      headerName: 'Status',
      field: 'emailStatus',
      suppressMenu: false,
      sortable: false,
      headerClass: 'left-aligned-cell-header',
      cellRenderer: (props) => {
        return props?.data?.emailStatus ? <>{statusObj[props?.value || 'awaiting_shipment']}</> : null;
      },
    },
  ];

  return (
    <>
      <Box>
        {/* Accordion for Logs Section */}
        <Accordion expanded={isExpanded}
          onChange={handleAccordionToggle}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
            <Typography variant='h4' fontWeight="semibold" style={{ color: "#2E2E3A" }}>Inventory Logs</Typography>
          </AccordionSummary>
          <AccordionDetails>
       
            {
              orderActivityLog === 'batch_logs' ?
                isExpanded && <AgGridTable col={columnDefs} getRows={getRows} mainGridApiRef={mainGridApiRef} pageRef={pageRef} isLoading={loading} style={{ height: '70vh' }} />
                :
                <OrderProductLogsActivity col={columnOrderDefs} getRows={getRows} mainGridApiRef={mainGridApiRef} pageRef={pageRef} isLoading={loading} style={{ height: '70vh' }} />
            }
          </AccordionDetails>
        </Accordion>
      </Box>
    </>
  );
}